/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { RATES_DATA_START } from 'frontend-utils/utils/constants/constant-values';
import { toLocalTimeInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import GeneralDatepickersDatepickerSharedTemplate from 'lumen/components/general/datepickers/datepicker-shared-template';
import { ROUTE_BENCHMARK_INSIGHT_INDEX } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { isFixedPeriod } from 'lumen/utils/dates/moment-helpers';
import { timeout } from 'lumen/utils/waiters';

interface GeneralDatepickersDatepickerRangeSignature {
  Args: {
    calendarFocusOnEnd: unknown;
    displayEvents: unknown;
    isApplyDisabled: unknown;
    maxDate: unknown;
    maxRange: unknown;
    minDate: unknown;
    nrOfDays: unknown;
    onCancel: unknown;
    onUpdateTempRange: unknown;
    rangeChanged: unknown;
    selectSingleDay: unknown;
    selectStandardRange: unknown;
    showMonthDropdowns: unknown;
    standardRange: unknown;
    triggerLabel: unknown;
  };
}

export default class GeneralDatepickersDatepickerRange extends Component<GeneralDatepickersDatepickerRangeSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Simple way of getting the calendar to refocus
  @tracked refocusMonths = 0;
  // Initialized in the did-insert in DatepickerSharedTemplate
  @tracked tempRange = null;

  // Made this a task because updating the range causes a rerender. Want to make sure the dropdown is closed first
  changeRangeTask = task({ restartable: true }, async (range, optionId) => {
    await timeout();
    if (this.args.isApplyDisabled === false || this.hasRangeChanged) {
      // @ts-expect-error: Incorrect type
      this.args.rangeChanged(range, optionId);
    }
    // For DatepickerWithCompare: updateCompare has to be executed after rangeChanged() for QP handling!
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.updateCompare?.();
  });

  // Make this with a timeout because the outside click would sometime fire after rerender wich made it close
  refocusCalendarTask = task({ drop: true }, async () => {
    await timeout();
    this.refocusMonths++;
  });

  /**
   * Check to see if we can move one period back
   * If it's a month, we check if the start of the month is before ratesDataStart
   * If it's a random period we subtract that one from the current start and check if we can go back
   * @return { boolean }   : have we reached the beginning?
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get beginReached() {
    const range = this.tempRange;
    // @ts-expect-error: Incorrect type
    if (range?.start && range?.end) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
      if (isFixedPeriod(range.start, range.end, 'month')) {
        return !this.isWithinRange(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          range.start.add(-1, 'month').startOf('month'),
        );
      }
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const period = range.start.diff(range.end, 'day') - 1;
      // @ts-expect-error: Incorrect type;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      return !this.isWithinRange(range.start.add(period, 'day'));
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canSelectMonth() {
    return (
      !this.args.showMonthDropdowns &&
      !this.args.selectSingleDay &&
      (!this.args.selectStandardRange || this.args.standardRange === 'month')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get convertedMaxDate() {
    // Convert Hotel TZ to browser TZ (and fall back on default Rate Insight max date)
    if (this.args.maxDate) {
      // @ts-expect-error: Incorrect type
      return toLocalTimeInBrowserTimezone(this.args.maxDate, this.timezone);
    }
    const ratesDataEnd = this.hotelAndMarketService.ratesDataEnd.endOf('month');
    // @ts-expect-error: Incorrect type
    return toLocalTimeInBrowserTimezone(ratesDataEnd, this.timezone);
  }

  // Since PowerCalendar is not able to work with timezones, we use browser TZ in this component (and its subcomponents)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get convertedMinDate() {
    // Convert Hotel TZ to browser TZ (and fall back on default Rate Insight min date)
    if (this.args.minDate) {
      // @ts-expect-error: Incorrect type
      return toLocalTimeInBrowserTimezone(this.args.minDate, this.timezone);
    }
    return dayjs(RATES_DATA_START);
  }

  // Explicitly observe range for the did-update in the shared template
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get convertedRange() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { start, end } = this.args.range;

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      start: toLocalTimeInBrowserTimezone(start, this.timezone),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      end: toLocalTimeInBrowserTimezone(end, this.timezone),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get convertedSidebarLists() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const sidebarLists = this.sidebarLists ?? this.args.sidebarLists;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    sidebarLists.forEach((sidebarList) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      sidebarList.options.forEach((sidebarOption) => {
        // Add a convertedRange to the list options in browser TZ for PowerCalendar (same as for convertedRange in DatepickerRange)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (sidebarOption.range) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          sidebarOption.convertedRange = {};
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          sidebarOption.convertedRange.start = sidebarOption.range.start
            ? toLocalTimeInBrowserTimezone(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                sidebarOption.range.start,
                this.hotelAndMarketService.timezone,
              )
            : null;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          sidebarOption.convertedRange.end = sidebarOption.range.end
            ? toLocalTimeInBrowserTimezone(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                sidebarOption.range.end,
                this.hotelAndMarketService.timezone,
              )
            : null;

          // Set isDisabled to true if the range is not within the min and max date.
          // Only do this when the isDisabled property is not set manually.
          // And only do this for options other than custom, because the custom option doesn't have a range.
          if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            sidebarOption.id !== 'custom' &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            (sidebarOption.isDisabled === null ||
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              sidebarOption.isDisabled === undefined)
          ) {
            const allow =
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.isWithinRange(sidebarOption.range.start) &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.isWithinRange(sidebarOption.range.end);

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            sidebarOption.isDisabled = !allow;
          }
        }
      });
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return sidebarLists;
  }

  /**
   * Check to see if we can move one period ahead
   * If it's a month, we check if the end of the month is before ratesDataEnd
   * If it's a random period we subtract that one from the current end and check if we can go further
   * @return { boolean }   : have we reached the end?
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endReached() {
    const range = this.tempRange;
    // @ts-expect-error: Incorrect type
    if (range?.start && range?.end) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
      if (isFixedPeriod(range.start, range.end, 'month')) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        return !this.isWithinRange(range.end.add(1, 'month').endOf('month'));
      }
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const period = range.end.diff(range.start, 'day') + 1;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      return !this.isWithinRange(range.end.add(period, 'day'));
    }
    return false;
  }

  @computed(
    'args.range.{end,start}',
    'convertedRange.{end,start}',
    'tempRange.{end,start}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRangeChanged() {
    // Check if tempRange is not null and tempRange is different from current range
    return (
      this.tempRange &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      (!this.convertedRange.start.isSame(this.tempRange.start) ||
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        !this.convertedRange.end.isSame(this.tempRange.end))
    );
  }

  @computed(
    'args.isApplyDisabled',
    'args.range.{end,start}',
    'convertedRange.{end,start}',
    'tempRange.{end,start}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isApplyDisabled() {
    if (this.args.isApplyDisabled !== undefined) {
      return this.args.isApplyDisabled;
    }

    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.convertedRange.start.isSame(this.tempRange.start) &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.convertedRange.end.isSame(this.tempRange.end)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timezone() {
    return this.hotelAndMarketService.timezone;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isWithinRange(moment) {
    if (this.convertedMinDate && this.convertedMaxDate) {
      return (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.convertedMinDate.isSameOrBefore(moment, 'day') &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.convertedMaxDate.isSameOrAfter(moment, 'day')
      );
    }
    return false;
  }

  /**
   * Will move the period one forward or one back according to the parameter
   * A period is either a month or the number of days between the start and end date
   * @param  { boolean } next: true if we want to move 1 period forward, false otherwise
   */
  // @ts-expect-error: Incorrect type
  movePeriod(next): void {
    // If we want to move forward, but we reached the end, do nothing
    // If we want to move backward, but we reached the beginning, do nothing
    const newRange = {};
    // @ts-expect-error: Incorrect type
    const { start } = this.tempRange;
    // tempRange.end is null when a single day is selected as range
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const end = this.tempRange.end ?? start;

    if (start && end) {
      const fixedPeriodName = ['month', 'quarter', 'year'].find((periodName) =>
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
        isFixedPeriod(start, end, periodName),
      );
      if (fixedPeriodName) {
        // If it's a fixed period, move one back
        let period = -1;

        if (next) {
          // If we want to move forward, reverse the period
          period *= -1;
        }

        // Allow moving by multiple periods at once:
        // e.g. when looking at 2 full months, on next we move 2 months further
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const periodLength = 1 + end.diff(start, fixedPeriodName);
        period *= periodLength;

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        newRange.start = start

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .add(period, fixedPeriodName)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .startOf(fixedPeriodName);
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        newRange.end = end.add(period, fixedPeriodName).endOf(fixedPeriodName);
      } else {
        // If it's not a fixed period, take the difference between the start and the end
        // This will be a negative number, so we will move back in time by default
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        let period = start.diff(end, 'day') - 1;

        if (next) {
          // If we want to move forward, reverse the sign
          period *= -1;
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        newRange.start = start.add(period, 'day');
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        newRange.end = end.add(period, 'day');
      }
    }
    this.updateTempRange(newRange);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.changeRangeTask.perform(newRange);
  }

  @action
  // @ts-expect-error: Incorrect type
  applyChanges(dropdown): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.changeRangeTask.perform(this.tempRange);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    dropdown.toggleDropdown();
  }

  @action
  // @ts-expect-error: Incorrect type
  cancelChanges(dropdown): void {
    this.onCancel();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    dropdown.toggleDropdown();
  }

  @action
  nextClicked(): void {
    if (!this.endReached) {
      this.movePeriod(true);

      const { currentRouteName } = this.router;

      if (currentRouteName === ROUTE_BENCHMARK_INSIGHT_INDEX) {
        this.usageAnalytics.track('Stay Date Range Changed', {
          eventTrigger: 'arrows',
          product: 'benchmark-insight',
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          amountOfDays: this.tempRange.end.diff(this.tempRange.start, 'days'),
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          startDaysOffsetFromToday: this.tempRange.start.diff(dayjs(), 'days'),
        });
      }
    }
  }

  @action
  onCancel(): void {
    this.updateTempRange(this.convertedRange);
    // @ts-expect-error: Incorrect type
    this.args.onCancel?.();
  }

  @action
  previousClicked(): void {
    if (!this.beginReached) {
      this.movePeriod(false);

      const { currentRouteName } = this.router;

      if (currentRouteName === ROUTE_BENCHMARK_INSIGHT_INDEX) {
        this.usageAnalytics.track('Stay Date Range Changed', {
          eventTrigger: 'arrows',
          product: 'benchmark-insight',
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          amountOfDays: this.tempRange.end.diff(this.tempRange.start, 'days'),
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          startDaysOffsetFromToday: this.tempRange.start.diff(dayjs(), 'days'),
        });
      }
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  selectRange(selectedRange, calendarShouldRefocus = false): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let { start, end } = selectedRange.moment;

    if (!this.isWithinRange(start)) {
      start = this.convertedMinDate;
    }

    if (end && !this.isWithinRange(end)) {
      end = this.convertedMaxDate;
    }

    const newRange = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      start,
    };

    if (this.args.selectSingleDay) {
      const { nrOfDays } = this.args;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      newRange.end = start.add(nrOfDays, 'day');
    } else if (this.args.selectStandardRange) {
      // When you can select only a full week, month, year, ...
      // Just pas for example: 'isoweek' as standardRange and we make sure this is selected
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      newRange.start = start.startOf(this.args.standardRange);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      newRange.end = start.endOf(this.args.standardRange);
    } else {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      newRange.end = end;
    }
    this.updateTempRange(newRange);
    // @ts-expect-error: Incorrect type
    this.args.onUpdateTempRange?.(newRange);
    // Needed this check because power calendar passes an extra argument
    if (calendarShouldRefocus) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.refocusCalendarTask.perform();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  updateTempRange(range): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (range.start) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.tempRange = range;
    }
  }

  <template>
    <GeneralDatepickersDatepickerSharedTemplate
      @applyChanges={{this.applyChanges}}
      @beginReached={{this.beginReached}}
      @calendarFocusOnEnd={{@calendarFocusOnEnd}}
      @canSelectMonth={{this.canSelectMonth}}
      @cancelChanges={{this.cancelChanges}}
      @displayEvents={{@displayEvents}}
      @endReached={{this.endReached}}
      {{! @glint-expect-error: Incorrect type }}
      @hasEventAccess={{this.access.hasEvents}}
      @isApplyDisabled={{this.isApplyDisabled}}
      @maxDate={{this.convertedMaxDate}}
      @maxRange={{@maxRange}}
      @minDate={{this.convertedMinDate}}
      @nextClicked={{this.nextClicked}}
      @onCancel={{this.onCancel}}
      @previousClicked={{this.previousClicked}}
      @range={{this.convertedRange}}
      @refocusMonths={{this.refocusMonths}}
      @selectRange={{this.selectRange}}
      @showMonthDropdowns={{@showMonthDropdowns}}
      @standardRange={{@standardRange}}
      @tempRange={{this.tempRange}}
      @triggerLabel={{@triggerLabel}}
      @updateTempRange={{this.updateTempRange}}
    />
  </template>
}
